import { ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/primitives';

/**
 * A "sort by" control: a field select plus a direction toggle.
 *
 * The card-list counterpart of header sorting: `DataTable` renders as a card list below `md`,
 * where there are no headers to click, so pages show this control there and clickable headers on
 * the table. Both drive the same state. The toggle is a real button with its state described for
 * screen readers, not an icon that swaps silently.
 */

/** One sortable field. */
export interface SortOption {
  /** Wire value, e.g. `AMOUNT`. */
  value: string;
  /** Translated label. */
  label: string;
}

/** Props for {@link SortControl}. */
export interface SortControlProps {
  /** Id for the select trigger, so the label reaches it. */
  id: string;
  /** Visible label, e.g. "Sort by". */
  label: string;
  /** The sortable fields. */
  options: readonly SortOption[];
  /** The selected field. */
  value: string;
  /** The current direction. */
  dir: 'ASC' | 'DESC';
  /** Field change. */
  onValueChange: (value: string) => void;
  /** Direction change — already toggled. */
  onDirChange: (dir: 'ASC' | 'DESC') => void;
  /** Additional classes — pages that also sort via table headers hide this control at `md`. */
  className?: string | undefined;
}

/** The field-plus-direction sort control — see the module docs above. */
export function SortControl({
  id,
  label,
  options,
  value,
  dir,
  onValueChange,
  onDirChange,
  className,
}: SortControlProps): ReactNode {
  const { t } = useTranslation();
  const next = dir === 'ASC' ? 'DESC' : 'ASC';
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-1.5">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger id={id} className="min-w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          aria-label={t(
            dir === 'ASC' ? 'layout.sortControl.ascending' : 'layout.sortControl.descending',
          )}
          onClick={() => {
            onDirChange(next);
          }}
        >
          {dir === 'ASC' ? (
            <ArrowUpNarrowWide aria-hidden="true" />
          ) : (
            <ArrowDownWideNarrow aria-hidden="true" />
          )}
        </Button>
      </div>
    </div>
  );
}
